import { useOutletContext } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	BtnLine,
	MoreStrip,
	Own,
	PanGrid,
	PanList,
	Sk,
	ViewBar,
	breakpoints,
} from '@quizzach/styles';
import { IcGrid, IcRows } from '@quizzach/assets';
import { UserQuizzesOutletContextType } from '@quizzach/interfaces';
import { QuizTicketCard, QuizTicketPanorama } from '@quizzach/components';
import { useWindowListener } from '@quizzach/contexts';

// two full desktop rows of four posters per load
const PAGE_SIZE = 8;
const VIEW_STORAGE_KEY = 'profileQuizzesView';

type QuizzesView = 'grid' | 'list';

export const UserQuizzes = () => {
	const { t } = useTranslation();

	const { loading, quizzesToList } =
		useOutletContext<UserQuizzesOutletContextType>();
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
	// cards from this index on came from »učitaj još« and fade in staggered
	const [freshFrom, setFreshFrom] = useState<number | null>(null);
	const [view, setView] = useState<QuizzesView>(() =>
		localStorage.getItem(VIEW_STORAGE_KEY) === 'grid' ? 'grid' : 'list'
	);

	// the panorama rows need desktop room: below the shell breakpoint the
	// shelf always falls back to the poster grid
	const { width } = useWindowListener();
	const isDesktop = width >= breakpoints.smallDesktop;
	const showList = isDesktop && view === 'list';

	const pickView = (next: QuizzesView) => {
		setView(next);
		// a view switch remounts every card — don't replay the load-more fade
		setFreshFrom(null);
		localStorage.setItem(VIEW_STORAGE_KEY, next);
	};

	const fadeProps = (index: number) =>
		freshFrom !== null && index >= freshFrom
			? {
					className: 'fade-new',
					style: { animationDelay: `${(index - freshFrom) * 45}ms` },
				}
			: {};

	const visibleQuizzes = quizzesToList?.slice(0, visibleCount);
	const remaining = Math.max(0, (quizzesToList?.length ?? 0) - visibleCount);

	const skeletons = (className: string, count: number) =>
		Array.from({ length: count }).map((_, index) => (
			<Sk key={index} className={className} aria-hidden='true' />
		));

	return (
		<Own data-id='user-quizzes-container'>
			<ViewBar>
				<div className='views'>
					<button
						type='button'
						aria-label={t('profile.quizzes.viewGrid')}
						title={t('profile.quizzes.viewGrid')}
						aria-pressed={!showList}
						onClick={() => pickView('grid')}
					>
						<IcGrid size={17} />
					</button>
					<button
						type='button'
						aria-label={t('profile.quizzes.viewList')}
						title={t('profile.quizzes.viewList')}
						aria-pressed={showList}
						onClick={() => pickView('list')}
					>
						<IcRows size={17} />
					</button>
				</div>
			</ViewBar>
			{showList ? (
				<PanList>
					{loading && skeletons('pan-sk', 3)}
					{!loading &&
						visibleQuizzes &&
						visibleQuizzes.map((quiz, index) => (
							<QuizTicketPanorama
								key={quiz.uuid}
								quiz={quiz}
								{...fadeProps(index)}
							/>
						))}
				</PanList>
			) : (
				<PanGrid>
					{loading && skeletons('pan-card-sk', 4)}
					{!loading &&
						visibleQuizzes &&
						visibleQuizzes.map((quiz, index) => (
							<QuizTicketCard
								key={quiz.uuid}
								quiz={quiz}
								{...fadeProps(index)}
							/>
						))}
				</PanGrid>
			)}
			{!loading && remaining > 0 && (
				<MoreStrip>
					<BtnLine
						type='button'
						onClick={() => {
							setFreshFrom(visibleCount);
							setVisibleCount((c) => c + PAGE_SIZE);
						}}
					>
						{t('premade.redesign.loadMore')}
					</BtnLine>
				</MoreStrip>
			)}
		</Own>
	);
};
